import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/App.css';
import { API_BASE_URL } from './utils/apiConfig';
import { ToastProvider } from './contexts/ToastContext';
import { InlineNoticeProvider } from './contexts/InlineNoticeContext';
import { useRoles } from './contexts/RolesContext';
import usePermissions from './utils/usePermissions';
import Login from './unauthenticated/Login';
import Register from './unauthenticated/SignIn';
import Home from './unauthenticated/Home';
import Features from './unauthenticated/Features';
import Contact from './unauthenticated/Contact';
import QuotationListPage from './authenticated/QuotationListPage';
import InvoiceListPage from './authenticated/InvoiceListPage';
import CustomerListPage from './authenticated/CustomerListPage';
import VendorListPage from './authenticated/VendorListPage';
import ProductListPage from './authenticated/ProductListPage';
import UserListPage from './authenticated/UserListPage';
import Reports from './authenticated/Reports';
import ExpenseListPage from './authenticated/ExpenseListPage';
import PurchaseListPage from './authenticated/PurchaseListPage';
import ClaimsListPage from './authenticated/ClaimsListPage';
import setupAxiosAuth from './utils/axiosAuth';
import AdminPanel from './components/AdminPanel';
import TabNavigator from './components/TabNavigator';
import Header from './components/Header';
import ChangePasswordModal from './components/ChangePasswordModal';

function App() {
  const { roles, refreshRoles, clearRoles } = useRoles();
  const { role } = usePermissions();
  // Initialize axios auth interceptor
  useEffect(() => {
    setupAxiosAuth();
  }, []);

  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return Boolean(localStorage.getItem('token'));
  });
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [currentUser, setCurrentUser] = useState(() => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  });
  const [showSignIn, setShowSignIn] = useState(false);
  const [route, setRoute] = useState(() => window.location.hash || '#/');

  const [adminPanelOpen, setAdminPanelOpen] = useState(false);
  const [currentLogo, setCurrentLogo] = useState(null);
  const [activeTab, setActiveTab] = useState('quotations');
  const [orgName, setOrgName] = useState('');
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  // Fetch current logo and organization name on app load
  useEffect(() => {
    const fetchCurrentLogo = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setCurrentLogo(null);
          return;
        }
        const requestId = `logo-current-app-${Date.now()}-${Math.round(Math.random() * 1e9)}`;
        const response = await axios.get(`${API_BASE_URL}/logo/current`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Request-Id': requestId,
            'X-Request-Source': 'app-initial'
          }
        });
        if (response.data.success && response.data.data) {
          setCurrentLogo(response.data.data);
        } else {
          setCurrentLogo(null);
        }
      } catch (error) {
        console.error('Error fetching current logo:', error);
      }
    };

    if (isAuthenticated) {
      fetchCurrentLogo();
      fetchOrganizationName();
    }
  }, [isAuthenticated]);

  // Simple hash-based routing (unauthenticated views)
  useEffect(() => {
    const onHash = () => setRoute(window.location.hash || '#/');
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Derive SignIn visibility from route when unauthenticated
  useEffect(() => {
    setShowSignIn(route === '#/register');
  }, [route]);

  // Load org name from backend on app mount if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchOrganizationName();
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      refreshRoles();
    }
  }, [isAuthenticated, refreshRoles]);

  const canViewPage = useCallback((pageId) => {
    if (!role) return false;

    const pages = roles?.roles?.[role]?.pages;
    const viewConfig = pages?.[pageId]?.view;
    const allowed = viewConfig?.intended ?? viewConfig?.current;

    if (pageId === 'admin' && ['admin', 'appAdmin'].includes(role)) {
      return allowed === undefined ? true : Boolean(allowed);
    }

    if (!pages) return false;
    return Boolean(allowed);
  }, [roles, role]);

  const visibleTabs = useMemo(() => {
    const allTabs = [
      'quotations',
      'invoices',
      'claims',
      'customers',
      'products',
      'vendors',
      'purchases',
      'expenses',
      'users',
      'reports',
      'admin'
    ];
    return allTabs.filter((tabId) => canViewPage(tabId));
  }, [canViewPage]);

  useEffect(() => {
    if (!isAuthenticated || visibleTabs.length === 0) return;
    if (!visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0]);
    }
  }, [isAuthenticated, visibleTabs, activeTab]);

  const handleLogin = () => {
    setIsAuthenticated(true);
    const user = localStorage.getItem('user');
    if (user) {
      setCurrentUser(JSON.parse(user));
    }
    // Fetch organization name from backend instead of localStorage
    fetchOrganizationName();
  };

  const fetchOrganizationName = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API_BASE_URL}/organisations/current`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data && response.data.name) {
        setOrgName(response.data.name);
        localStorage.setItem('organisationName', response.data.name);
      }
      if (response.data?.metadata?.numberingSystem) {
        localStorage.setItem('numberingSystem', response.data.metadata.numberingSystem);
      }
      localStorage.setItem('currencyCode', response.data?.metadata?.currency?.code || 'INR');
      localStorage.setItem('currencyPrefix', response.data?.metadata?.currency?.prefix || 'Rs.');
      localStorage.setItem('roundOffTotals', response.data?.metadata?.roundOffTotals ? 'true' : 'false');
    } catch (error) {
      console.error('Error fetching organization name:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('organisationId');
    localStorage.removeItem('organisationName');
    localStorage.removeItem('numberingSystem');
    localStorage.removeItem('currencyCode');
    localStorage.removeItem('currencyPrefix');
    localStorage.removeItem('roundOffTotals');
    clearRoles();
    setIsAuthenticated(false);
    setShowUserDropdown(false);
    setCurrentUser(null);
    setOrgName('');
    setCurrentLogo(null);
  };

  const handleLogoChange = useCallback((logoData) => {
    setCurrentLogo(logoData);
  }, []);

  const handleOpenChangePassword = () => {
    setShowUserDropdown(false);
    setChangePasswordOpen(true);
  };

  const handlePasswordChanged = () => {
    setChangePasswordOpen(false);
    handleLogout();
    window.location.hash = '#/login';
  };

  return (
    <ToastProvider>
    <InlineNoticeProvider>
    <div className="app-root">
      <Header
        isAuthenticated={isAuthenticated}
        currentLogo={currentLogo}
        orgName={orgName}
        currentUser={currentUser}
        showUserDropdown={showUserDropdown}
        onUserDropdownToggle={setShowUserDropdown}
        onLogout={handleLogout}
        onChangePassword={handleOpenChangePassword}
      />
      <ChangePasswordModal
        isOpen={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
        onSuccess={handlePasswordChanged}
      />
      <main className="app-main">
        {isAuthenticated ? (
          <div className="content-wrapper">
            <TabNavigator
              activeTab={activeTab}
              onTabChange={setActiveTab}
              visibleTabs={visibleTabs}
            />
            <div className="tab-content">
              {activeTab === 'quotations' && canViewPage('quotations') && (
                <QuotationListPage currentLogo={currentLogo} />
              )}
              {activeTab === 'invoices' && canViewPage('invoices') && (
                <InvoiceListPage currentLogo={currentLogo} />
              )}
              {activeTab === 'claims' && canViewPage('claims') && (
                <ClaimsListPage />
              )}
              {activeTab === 'customers' && canViewPage('customers') && (
                <CustomerListPage />
              )}
              {activeTab === 'products' && canViewPage('products') && (
                <ProductListPage />
              )}
              {activeTab === 'vendors' && canViewPage('vendors') && (
                <VendorListPage />
              )}
              {activeTab === 'purchases' && canViewPage('purchases') && (
                <PurchaseListPage />
              )}
              {activeTab === 'expenses' && canViewPage('expenses') && (
                <ExpenseListPage />
              )}
              {activeTab === 'users' && canViewPage('users') && (
                <UserListPage />
              )}
              {activeTab === 'reports' && canViewPage('reports') && (
                <Reports />
              )}
              {activeTab === 'admin' && canViewPage('admin') && (
                <AdminPanel 
                  isOpen={true}
                  onClose={() => setActiveTab('quotations')}
                  embedded={true}
                  onLogoChange={handleLogoChange}
                />
              )}
              {visibleTabs.length === 0 && (
                <div style={{ padding: '16px' }}>
                  Loading permissions...
                </div>
              )}
            </div>
          </div>
        ) : (
          <div>
            {route === '#/register' ? (
              <Register />
            ) : route === '#/features' ? (
              <Features />
            ) : route === '#/contact' ? (
              <Contact />
            ) : route === '#/login' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center', marginTop: '24px' }}>
                <Login onLogin={handleLogin} />
              </div>
            ) : (
              <Home />
            )}
          </div>
        )}
      </main>
      <div className="app-version">v1.14</div>
    </div>
    </InlineNoticeProvider>
    </ToastProvider>
  );
}

export default App;
